import React, { useRef, useState, lazy, Suspense } from "react";
import { GeneratedDataItem } from "./types";
import uploadFileToS3 from "../../../utils/uploadFileToS3";
import { toast } from "react-toastify";
import ImageSection from "./ImageSection";

// Lazy load image panels
const ImageGeneratorPanel = lazy(() => import("./Imagegeneratorpanel"));
const ImageSearchPanel = lazy(() => import("./imagesearchpanel"));

interface ImageComponentProps {
  item: GeneratedDataItem;
  editMode: boolean;
  editData: Record<string, any>;
  handleInputChange: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
}

interface IImageDataObject {
  title: string;
  component_id: string;
  header1: string;
  header2: string;
  header3: string;
  header4: string;
  imageUrl1: string;
  imageUrl2: string;
  imageUrl3: string;
  imageUrl4: string;
  overview: string;
  size: number;
  colors?: object;
  citation?: string;
  [key: string]: string | number | object | undefined;
}

type ImageDataType = string | IImageDataObject;

const ImageComponent: React.FC<ImageComponentProps> = ({
  item,
  editMode,
  editData,
  handleInputChange,
}) => {
  const id = item._id || "";
  const imageData = editData?.image || item.image || "";
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [activePanel, setActivePanel] = useState<
    "image-generate" | "image-search" | null
  >(null);
  const [selectedImage, setSelectedImage] = useState<string>("image");

  // Handle multiple images for narrative case
  const isMultipleImages = id === "narrative_image_Image";
  const isSingleImageNarrative = id === "narrative_image_TextandImage";
  const isNarrativeContext = isMultipleImages || isSingleImageNarrative;
  const maxImages = isMultipleImages ? 4 : isSingleImageNarrative ? 1 : 1;
  const imageUrls = isNarrativeContext
    ? imageData
      ? imageData.split(",").filter(Boolean)
      : []
    : typeof imageData === "string"
    ? [imageData].filter(Boolean)
    : typeof imageData === "object" && imageData
    ? ["imageUrl1", "imageUrl2", "imageUrl3", "imageUrl4"]
        .map(
          (key) =>
            (imageData as IImageDataObject)[key as keyof IImageDataObject]
        )
        .filter(
          (url): url is string => typeof url === "string" && url.trim() !== ""
        )
    : [];
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const imageKeys = [
    "imageUrl1",
    "imageUrl2",
    "imageUrl3",
    "imageUrl4",
  ] as const;
  const filledImages = imageKeys.filter((key) => {
    const url = (imageData as IImageDataObject)?.[key];
    return url && url.trim() !== "";
  });

  const [addingSlot, setAddingSlot] = useState<number | null>(null); // index of the new empty slot

  const handleAddImageSlot = () => {
    const emptyIndex = imageKeys.findIndex(
      (key) => !(imageData as IImageDataObject)?.[key]?.trim()
    );
    if (emptyIndex !== -1) setAddingSlot(emptyIndex);
  };

  const handleRemoveImage = (index: number) => {
    if (isNarrativeContext) {
  const newUrls = imageUrls.filter((_value: string, i: number) => i !== index);
      handleInputChange(id, "image", newUrls.join(","), "Image");
    }
  };

  if (fileInputRefs.current.length !== 4) {
    fileInputRefs.current = Array(4)
      .fill(null)
      .map((_, i) => fileInputRefs.current[i] || null);
  }

  if (editMode) {
    return (
      <div
        className={`relative w-full rounded p-2 ${
          isNarrativeContext ? "flex flex-col lg:flex-row gap-4" : ""
        }`}
      >
        <>
          {isNarrativeContext ? (
            <>
              {/* Left Half - Images */}
              <div className="w-full lg:w-2/3">
                <ImageSection
                  imageUrls={imageUrls}
                  maxImages={maxImages}
                  isMultipleImages={isMultipleImages}
                  onRemoveImage={handleRemoveImage}
                />
              </div>
              <div className="w-full lg:w-1/3 flex flex-col gap-3">
                {/* Right Half - Action Buttons */}
                {/* Always show buttons - centered when no panel is active */}
                <div className={`flex gap-2 mb-4 ${!activePanel ? 'justify-center items-center min-h-[200px]' : ''}`}>
                  {/* Upload Button */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      if (isNarrativeContext && imageUrls.length >= maxImages) {
                        toast.error(
                          `Maximum ${maxImages} image${
                            maxImages > 1 ? "s" : ""
                          } allowed`
                        );
                        return;
                      }
                      try {
                        setUploading(true);
                        const url = await uploadFileToS3({
                          name: f.name,
                          type: f.type,
                          body: f,
                        });
                        const newUrls = [...imageUrls.filter(Boolean), url];
                        handleInputChange(
                          id,
                          "image",
                          newUrls.join(","),
                          "Image"
                        );
                        toast.success("Image uploaded");
                      } catch (err) {
                        console.error("Upload failed", err);
                        toast.error("Upload failed");
                      } finally {
                        setUploading(false);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      fileInputRef.current?.click();
                    }}
                    disabled={
                      uploading ||
                      (isNarrativeContext && imageUrls.length >= maxImages)
                    }
                    className={`px-6 py-2 rounded-lg border border-gray-300 text-sm font-medium transition-colors ${
                      uploading ||
                      (isNarrativeContext && imageUrls.length >= maxImages)
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {uploading
                      ? "Uploading..."
                      : isNarrativeContext && imageUrls.length >= maxImages
                      ? `Max ${maxImages}`
                      : "Upload"}
                  </button>
                  <button
                    onClick={() => {
                      console.log("Set selected image to image");
                      setSelectedImage("image");
                      setActivePanel("image-search");
                    }}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      console.log("Set selected image to image");
                      setSelectedImage("image");
                      setActivePanel("image-generate");
                    }}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Generate
                  </button>
                </div>

                {/* Panel appears below buttons when active - constrained to right side */}
                {activePanel && (
                  <div className="w-full h-96 bg-white border rounded-lg p-4 overflow-hidden">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">
                        {activePanel === "image-generate"
                          ? "Image Generator"
                          : "Image Search"}
                      </h4>
                      <button
                        onClick={() => setActivePanel(null)}
                        className="px-2 py-1 text-sm rounded text-gray-600 hover:bg-gray-100"
                      >
                        Back
                      </button>
                    </div>
                    <div className="h-full overflow-y-auto">
                      <Suspense
                        fallback={
                          <div className="text-center p-4">Loading...</div>
                        }
                      >
                        {activePanel === "image-generate" ? (
                          <ImageGeneratorPanel
                            onBack={() => setActivePanel(null)}
                            onSelectImage={(imageUrl) => {
                              const newUrls = [
                                ...imageUrls.filter(Boolean),
                                imageUrl,
                              ];
                              handleInputChange(
                                id,
                                selectedImage,
                                selectedImage === "image"
                                  ? newUrls.join(",")
                                  : imageUrl,
                                "Image"
                              );
                              setActivePanel(null);
                            }}
                          />
                        ) : (
                          <ImageSearchPanel
                            onBack={() => setActivePanel(null)}
                            onSelectImage={(imageUrl) => {
                              const newUrls = [
                                ...imageUrls.filter(Boolean),
                                imageUrl,
                              ];
                              handleInputChange(
                                id,
                                selectedImage,
                                selectedImage === "image"
                                  ? newUrls.join(",")
                                  : imageUrl,
                                "Image"
                              );
                              setActivePanel(null);
                            }}
                          />
                        )}
                      </Suspense>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {typeof imageData === "string" && imageData && (
                <>
                  <input
                    type="text"
                    value={imageData}
                    onChange={(e) =>
                      handleInputChange(id, "image", e.target.value, "Image")
                    }
                    className="w-full p-2 border border-gray-300 rounded mb-3"
                    placeholder="Image URL"
                  />
                  <div className="mt-2 text-center">
                    <img
                      src={imageData}
                      alt="Preview"
                      className="max-w-full max-h-[200px] mx-auto object-contain border border-gray-200 rounded"
                    />
                  </div>
                </>
              )}

              {typeof imageData === "object" && imageData && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {["imageUrl1", "imageUrl2", "imageUrl3", "imageUrl4"].map(
                      (key, index) => {
                        const url = (imageData as IImageDataObject)[
                          key as keyof IImageDataObject
                        ];

                        if (url) {
                          return (
                            <div className={`flex flex-col p-2`}>
                              <img
                                src={String(url)}
                                alt={key}
                                className="w-full h-32 object-contain border border-gray-200 rounded"
                              />

                              <div
                                key={`${key}-${index}`}
                                className="flex flex-wrap justify-center gap-1 mt-2"
                              >
                                <input
                                  ref={(el) => {
                                    fileInputRefs.current[index] = el; // ✅ TypeScript now knows this is fine
                                  }}
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  onChange={async (e) => {
                                    const f = e.target.files?.[0];
                                    if (!f) return;

                                    try {
                                      setUploading(true);
                                      const url = await uploadFileToS3({
                                        name: f.name,
                                        type: f.type,
                                        body: f,
                                      });

                                      if (isNarrativeContext) {
                                        const newUrls = [...imageUrls];
                                        newUrls[index] = url;
                                        handleInputChange(
                                          id,
                                          "image",
                                          newUrls.join(","),
                                          "Image"
                                        );
                                      } else if (
                                        typeof imageData === "object" &&
                                        imageData
                                      ) {
                                        (imageData as IImageDataObject)[key] =
                                          url;
                                        handleInputChange(
                                          id,
                                          `image.${key}`,
                                          url,
                                          "Image"
                                        );
                                      } else {
                                        handleInputChange(
                                          id,
                                          "image",
                                          url,
                                          "Image"
                                        );
                                      }

                                      toast.success("Image uploaded");
                                    } catch (err) {
                                      console.error("Upload failed", err);
                                      toast.error("Upload failed");
                                    } finally {
                                      setUploading(false);
                                      if (fileInputRefs.current[index])
                                        fileInputRefs.current[index]!.value =
                                          "";
                                    }
                                  }}
                                />

                                {/* <button onClick={() => fileInputRefs.current[index]?.click()}>
      Upload {key}
    </button>
  </div> */}

                                <button
                                  onClick={() =>
                                    fileInputRefs.current[index]?.click()
                                  }
                                  disabled={
                                    uploading ||
                                    (isNarrativeContext &&
                                      imageUrls.length >= maxImages)
                                  }
                                  className={`px-2 py-1 text-xs rounded border border-gray-300 ${
                                    uploading ||
                                    (isNarrativeContext &&
                                      imageUrls.length >= maxImages)
                                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                      : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                                  }`}
                                >
                                  {uploading
                                    ? "Uploading..."
                                    : isNarrativeContext &&
                                      imageUrls.length >= maxImages
                                    ? `Max ${maxImages}`
                                    : "Upload"}
                                </button>

                                <button
                                  onClick={() => {
                                    setSelectedImage(`image.${key}`);
                                    setActivePanel("image-search");
                                  }}
                                  className="px-2 py-1 text-xs rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                                >
                                  Search
                                </button>

                                <button
                                  onClick={() => {
                                    setSelectedImage(`image.${key}`);
                                    setActivePanel("image-generate");
                                  }}
                                  className="px-2 py-1 text-xs rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300"
                                >
                                  Generate
                                </button>
                              </div>
                            </div>
                          );
                        }
                        if (addingSlot === index) {
                          return (
                            <div
                              key={key}
                              className="flex flex-col items-center gap-2"
                            >
                              <div className="w-32 h-32 border border-gray-200 rounded flex items-center justify-center text-gray-400">
                                Preview
                              </div>

                              <input
                                ref={(el) => {
                                  fileInputRefs.current[index] = el;
                                }}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={async (e) => {
                                  const f = e.target.files?.[0];
                                  if (!f) return;

                                  try {
                                    setUploading(true);
                                    const uploadedUrl = await uploadFileToS3({
                                      name: f.name,
                                      type: f.type,
                                      body: f,
                                    });
                                    (imageData as IImageDataObject)[key] =
                                      uploadedUrl;
                                    handleInputChange(
                                      id,
                                      `image.${key}`,
                                      uploadedUrl,
                                      "Image"
                                    );
                                    toast.success("Image uploaded");
                                    setAddingSlot(null); // reset slot if needed
                                  } catch (err) {
                                    console.error("Upload failed", err);
                                    toast.error("Upload failed");
                                  } finally {
                                    setUploading(false);
                                    if (fileInputRefs.current[index])
                                      fileInputRefs.current[index]!.value = "";
                                  }
                                }}
                              />

                              <div className="flex gap-2">
                                <button
                                  onClick={() =>
                                    fileInputRefs.current[index]?.click()
                                  }
                                  disabled={uploading}
                                  className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                >
                                  Upload
                                </button>
                                <button
                                  onClick={() => setActivePanel("image-search")}
                                  className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                >
                                  Search
                                </button>
                                <button
                                  onClick={() =>
                                    setActivePanel("image-generate")
                                  }
                                  className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                                >
                                  Generate
                                </button>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }
                    )}
                  </div>

                  {imageKeys.filter(
                    (key) =>
                      (imageData as IImageDataObject)?.[key]?.trim() !== ""
                  ).length < 4 && (
                    <button
                      onClick={handleAddImageSlot}
                      disabled={uploading}
                      className="px-4 py-2 rounded border bg-gray-100 hover:bg-gray-200"
                    >
                      Add Image
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Non-narrative context buttons */}
          {!isNarrativeContext &&
            typeof imageData === "string" &&
            activePanel !== "image-generate" &&
            activePanel !== "image-search" && (
              <div className="flex justify-center gap-2 mt-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    try {
                      setUploading(true);
                      const url = await uploadFileToS3({
                        name: f.name,
                        type: f.type,
                        body: f,
                      });
                      handleInputChange(id, "image", url, "Image");
                      toast.success("Image uploaded");
                    } catch (err) {
                      console.error("Upload failed", err);
                      toast.error("Upload failed");
                    } finally {
                      setUploading(false);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }
                  }}
                />
                <button
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                  disabled={uploading}
                  className={`px-3 py-1 rounded border border-gray-300 max-h-[50px] ${
                    uploading
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
                <button
                  onClick={() => {
                    setSelectedImage("image");
                    setActivePanel("image-search");
                  }}
                  className="px-3 py-1 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 max-h-[50px]"
                >
                  Search
                </button>
                <button
                  onClick={() => {
                    setSelectedImage("image");
                    setActivePanel("image-generate");
                  }}
                  className="px-3 py-1 rounded text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 max-h-[50px]"
                >
                  Generate
                </button>
              </div>
            )}
        </>

        {activePanel === "image-generate" && !isNarrativeContext && (
          <div className="image-generator-panel">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Image Generator</h4>
              <button
                onClick={() => setActivePanel(null)}
                className="px-2 py-1 text-sm rounded text-gray-600 hover:bg-gray-100"
              >
                Back
              </button>
            </div>
            <Suspense
              fallback={
                <div className="text-center p-4">
                  Loading image generator...
                </div>
              }
            >
              <ImageGeneratorPanel
                onBack={() => setActivePanel(null)}
                onSelectImage={(imageUrl) => {
                  const newUrls = [...imageUrls.filter(Boolean), imageUrl];
                  handleInputChange(
                    id,
                    selectedImage,
                    selectedImage == "image" ? newUrls.join(",") : imageUrl,
                    "Image"
                  );
                  setActivePanel(null);
                }}
              />
            </Suspense>
          </div>
        )}

        {activePanel === "image-search" && !isNarrativeContext && (
          <div className="image-search-panel">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Image Search</h4>
              <button
                onClick={() => setActivePanel(null)}
                className="px-2 py-1 text-sm rounded text-gray-600 hover:bg-gray-100"
              >
                Back
              </button>
            </div>
            <Suspense
              fallback={
                <div className="text-center p-4">Loading image search...</div>
              }
            >
              <ImageSearchPanel
                onBack={() => setActivePanel(null)}
                onSelectImage={(imageUrl) => {
                  const newUrls = [...imageUrls.filter(Boolean), imageUrl];
                  handleInputChange(
                    id,
                    selectedImage,
                    selectedImage == "image" ? newUrls.join(",") : imageUrl,
                    "Image"
                  );
                  setActivePanel(null);
                }}
              />
            </Suspense>
          </div>
        )}
      </div>
    );
  }

  const convertGoogleDriveUrl = (url: string): string => {
    if (url.includes("drive.google.com")) {
      // Extract file ID from various Google Drive URL formats
      let fileId = "";

      if (url.includes("/file/d/")) {
        fileId = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)?.[1] || "";
      } else if (url.includes("id=")) {
        fileId = url.match(/id=([a-zA-Z0-9_-]+)/)?.[1] || "";
      }

      if (fileId) {
        return `https://drive.google.com/thumbnail&id=${fileId}`;
      }
      console.log(fileId);
    }
    return url;
  };

  // Render image for viewing
  return (
    <div className="w-full p-2">
      {imageData ? (
        typeof imageData === "string" ? (
          <img
            src={imageData}
            alt="Image"
            className="rounded-md w-full h-auto"
          />
        ) : (
          <div className="w-full p-2">
            <h4 className="text-lg font-bold mb-2">
              {imageData.title || "Images"}
            </h4>
            <div className={`grid gap-3 w-full ${
              (() => {
                const validImages = ['imageUrl1', 'imageUrl2', 'imageUrl3', 'imageUrl4']
                  .map((key) => (imageData as any)[key])
                  .filter((url) => url && typeof url === 'string' && url.trim().length > 0 && url !== 'null' && url !== 'undefined');
                const count = validImages.length;
                return count === 1 ? 'grid-cols-1' :
                       count === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                       count === 3 ? 'grid-cols-2 sm:grid-cols-3' :
                       'grid-cols-2 sm:grid-cols-4';
              })()
            }`}>
              {["imageUrl1", "imageUrl2", "imageUrl3", "imageUrl4"]
                .map((key) => (imageData as any)[key])
                .filter((url) => {
                  return (
                    url &&
                    typeof url === "string" &&
                    url.trim().length > 0 &&
                    url !== "null" &&
                    url !== "undefined"
                  );
                })
                .map((url, index) => (
                  <div key={index}>
                    <img
                      src={convertGoogleDriveUrl(url)}
                      alt={`${imageData.title || "Image"}-${index + 1}`}
                      className="rounded-md w-full h-auto object-cover border border-gray-200"
                      onError={(e) => {
                        // Fallback: try to open the original URL in a new tab
                        console.error("Failed to load image:", url);
                        e.currentTarget.style.display = "none";
                        // You could also show a placeholder image here
                      }}
                    />
                  </div>
                ))}
            </div>
          </div>
        )
      ) : (
        <div className="w-full h-32 bg-gray-100 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center">
          <p className="text-gray-500">No image uploaded</p>
        </div>
      )}
    </div>
  );
};

export default ImageComponent;